/**
 * « Insérer un schéma » dans un bloc de leçon : le tuteur décrit la figure,
 * l'IA renvoie des visuels structurés (edge fn `lesson-figure-generate`),
 * aperçu, puis insertion en fin de texte sous forme de bloc ```jr-visual```
 * (rendu en figure par `LessonMarkdown` côté élève).
 */

import { useState } from 'react';
import { Shapes } from 'lucide-react';
import { Button, VisualBlockRenderer } from '@jeraisonne/ui';
import type { ExerciseVisualBlock } from '@jeraisonne/ai/visual-blocks';
import { lessonVisualFence } from '@jeraisonne/db';
import { supabase } from '@/lib/supabase';

/** Ajoute les schémas à la fin d'un texte markdown (pur, testé). */
export function appendLessonVisuals(
  md: string,
  blocks: readonly ({ type: string } & Record<string, unknown>)[],
): string {
  const fences = blocks.map((b) => lessonVisualFence(b)).join('\n\n');
  const base = md.trimEnd();
  return base ? `${base}\n\n${fences}` : fences;
}

export function InsertLessonFigure({
  contextMd,
  disabled,
  onInsert,
}: {
  contextMd: string;
  disabled?: boolean;
  onInsert: (blocks: ({ type: string } & Record<string, unknown>)[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<({ type: string } & Record<string, unknown>)[] | null>(
    null,
  );

  async function generate() {
    setPending(true);
    setError(null);
    setPreview(null);
    const { data, error: err } = await supabase.functions.invoke<{
      visual_blocks?: ({ type: string } & Record<string, unknown>)[];
    }>('lesson-figure-generate', {
      body: { description, context_md: contextMd.slice(0, 8000) },
    });
    setPending(false);
    if (err || !data?.visual_blocks?.length) {
      setError('Le schéma n’a pas pu être généré. Précise la description et réessaie.');
      return;
    }
    setPreview(data.visual_blocks);
  }

  if (!open) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="text-primary-600 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold disabled:opacity-50"
      >
        <Shapes className="h-4 w-4" aria-hidden /> Insérer un schéma
      </button>
    );
  }

  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
      <label className="block text-xs font-semibold text-slate-700">
        Décris la figure (points, longueurs, angles, courbe, tableau…)
        <textarea
          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Triangle ABC rectangle en A, AB = 3 cm, AC = 4 cm, hypoténuse BC en rouge"
        />
      </label>
      {error && (
        <p role="alert" className="text-accent-600 text-xs">
          {error}
        </p>
      )}
      {preview && (
        <div className="space-y-2 rounded-lg bg-white p-2">
          {preview.map((b, i) => (
            <div key={i} className="overflow-x-auto">
              <VisualBlockRenderer block={b as unknown as ExerciseVisualBlock} />
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setOpen(false);
            setPreview(null);
            setError(null);
          }}
        >
          Annuler
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => void generate()}
          loading={pending}
          disabled={description.trim().length < 3}
        >
          {preview ? 'Régénérer' : 'Générer le schéma'}
        </Button>
        {preview && (
          <Button
            type="button"
            onClick={() => {
              onInsert(preview);
              setOpen(false);
              setPreview(null);
              setDescription('');
            }}
          >
            Insérer dans le texte
          </Button>
        )}
      </div>
    </div>
  );
}
